import { Select } from "antd";
import { getCookie } from "cookies-next";
import { useMemo, useState } from "react";
import { useTranslation } from "next-i18next";
import { useInterval } from "react-use";
import styled from "styled-components";

import { poppins } from "@/components/common/fonts";
import { IconCheck } from "@/components/common/Icons";
import { ProjectForPublishScorm } from "@/types/scorm";

export const VersionSelect = ({
  projectId,
  versions,
  onChange,
  disabled,
  loading,
  readOnly,
  isHiddenPublished,
}: {
  projectId?: string;
  versions?: ProjectForPublishScorm[];
  onChange?: (projectId: string) => void;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  isHiddenPublished?: boolean;
}) => {
  const { t } = useTranslation("common");

  const options = useMemo(() => {
    return (
      versions
        ?.filter(({ exportType }) => exportType === "video")
        .map(({ _id, version }) => ({
          value: _id,
          label: version,
        })) ?? []
    );
  }, [versions]);

  const selectedVersion = useMemo(() => {
    return (
      versions?.find((version) => version._id === projectId)?.version ?? null
    );
  }, [projectId, versions]);

  if (readOnly || options.length < 2) {
    return (
      <DummySelect>
        {t("Ver. {{version}}", { version: selectedVersion })}
      </DummySelect>
    );
  }

  return (
    <StyledSelect
      options={options}
      value={projectId}
      onChange={onChange}
      disabled={disabled}
      loading={loading}
      popupMatchSelectWidth={false}
      placement="bottomRight"
      labelRender={() => {
        return <div>{t("Ver. {{version}}", { version: selectedVersion })}</div>;
      }}
      optionRender={(option) => {
        const current = versions?.find(
          (version) => version._id === option.value,
        );

        if (!current) {
          return null;
        }

        const selected = current._id === projectId;

        return (
          <Option
            selected={selected}
            option={current}
            isHiddenPublished={isHiddenPublished}
          />
        );
      }}
      dropdownStyle={{
        borderRadius: "6px",
        background: "#FFF",
        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
        padding: 0,
      }}
    />
  );
};

const DummySelect = styled.div`
  flex-shrink: 0;

  width: 100px;
  height: 37px;
  padding: 8px 15px;

  color: #333;

  /* sub_title_h3 */
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const StyledSelect = styled(Select<string>)`
  flex-shrink: 0;

  width: 100px;
  height: 37px;

  & .ant-select-selection-item {
    color: #333;

    /* sub_title_h3 */
    font-family: ${poppins.string};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  && .ant-select-selector {
    border: 0;
    padding: 8px 15px;
  }

  & .ant-select-arrow {
    right: 15px;
  }
`;

const Option = ({
  selected,
  option,
  isHiddenPublished,
}: {
  selected: boolean;
  option: ProjectForPublishScorm;
  isHiddenPublished?: boolean;
}) => {
  const locale = (getCookie("NEXT_LOCALE") as string) ?? "en";

  const { t } = useTranslation("common");

  const [now, setNow] = useState(() => Date.now());

  useInterval(() => {
    setNow(Date.now());
  }, 1000 * 10);

  const humanReadableDate = useMemo(() => {
    const createdDate = new Date(option.createdAt);
    const diff = now - createdDate.getTime();
    const diffSeconds = Math.floor(diff / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return createdDate.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
        hour12: true,
        hour: "numeric",
        minute: "numeric",
      });
    } else if (diffHours > 0) {
      return t(`{{hours}} hours ago`, { hours: diffHours });
    } else {
      return t(`{{minutes}} minutes ago`, { minutes: diffMinutes });
    }
  }, [now, option.createdAt, locale, t]);

  return (
    <OptionContainer>
      <OptionCheck>
        {selected && (
          <IconCheck
            style={{
              width: "24px",
              height: "24px",
            }}
          />
        )}
      </OptionCheck>
      <OptionText>
        <div className="version">
          {t("Version {{version}}", {
            version: option.version,
          })}
          {option.isPublishedScorm && !isHiddenPublished && (
            <span className="published">{t("Published")}</span>
          )}
        </div>
        <div className="date">{humanReadableDate}</div>
      </OptionText>
    </OptionContainer>
  );
};

const OptionContainer = styled.div`
  display: flex;
  gap: 10px;

  padding: 0 5px;
`;

const OptionCheck = styled.div`
  width: 24px;
`;

/**
 * - & .version
 * - & .version .published
 * - & .date
 */
const OptionText = styled.div`
  .rc-virtual-list-holder-inner:has(&) {
    padding: 0 10px;
  }

  .ant-select-item:has(&) {
    padding: 0;
    margin-bottom: 10px;

    &:first-child {
      margin-top: 10px;
    }
  }

  display: flex;
  flex-direction: column;

  color: #000;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */

  & .version {
    display: flex;
    gap: 10px;
    align-items: center;

    height: 24px;

    .published {
      border-radius: 4px;
      background: #e1eaff;
      padding: 2px 10px;

      color: #4276ee;
      font-family: ${poppins.string};
      font-size: 11px;
      font-style: normal;
      font-weight: 500;
      line-height: 16px; /* 145.455% */
    }
  }

  & .date {
    display: flex;
    align-items: center;

    height: 24px;

    color: #777;
  }
`;
